import { Formio } from 'formiojs';
import { Token } from './models/token';
import { FormioToken } from './models/formio-token';

/**
 * Persists Keycloak tokens to localStorage and keeps Form.io's static token
 * cache in sync.
 *
 * Form.io's `Formio.getToken()` reads `localStorage.formioToken` once and
 * afterwards serves the in-memory `Formio.tokens` cache. Writing the
 * refreshed token to localStorage alone therefore leaves every
 * `Formio.makeRequest` call (form action buttons / bot calls) on the
 * page-load token until it expires (TOBE-17874). `Formio.setToken()` is
 * avoided on purpose — it triggers a `Formio.currentUser()` request as a
 * side effect.
 */
export function persistKeycloakTokens(kcInstance: any): void {
  const token = kcInstance.token;
  if (!token) {
    return;
  }

  try {
    localStorage.setItem(Token.tokenNameInStorage, token);
    localStorage.setItem(
      FormioToken.tokenNameInStorage,
      FormioToken.scheme + token
    );
    localStorage.setItem(
      Token.refreshTokenNameInStorage,
      kcInstance.refreshToken ?? ''
    );
    syncFormioTokenCache(FormioToken.scheme + token);
  } catch (e) {
    console.error('Failed to persist Keycloak tokens early:', e);
  }
}

/**
 * Removes Keycloak tokens from localStorage and the Form.io token cache.
 */
export function clearKeycloakTokens(): void {
  try {
    localStorage.removeItem(Token.tokenNameInStorage);
    localStorage.removeItem(FormioToken.tokenNameInStorage);
    localStorage.removeItem(Token.refreshTokenNameInStorage);
    syncFormioTokenCache(null);
  } catch {
    // Intentionally ignore storage errors (e.g., private mode)
  }
}

function syncFormioTokenCache(value: string | null): void {
  const tokens = (Formio as any).tokens;
  if (!tokens) {
    // Nothing cached yet — the next Formio.getToken() lazily reads the
    // value just written to localStorage.
    return;
  }
  if (value === null) {
    delete tokens[FormioToken.tokenNameInStorage];
  } else {
    tokens[FormioToken.tokenNameInStorage] = value;
  }
}
